<template>
  <div class="contracts-dictionary">
    <PageTitle hideMarginLeft>Справочник договоров — Статус отправки в 1С</PageTitle>

    <div class="contracts-dictionary__filters tw-flex tw-flex-wrap tw-items-end tw-gap-3 tw-mb-4">
      <CustomDateRange
        :value="filters.dateRange"
        label="Период"
        @change_range="onDateRangeChange"
      />

      <CustomSelect
        :value="filters.send_status"
        :options="SEND_STATUS_OPTIONS"
        label="Статус отправки"
        option-value-key="value"
        option-label-key="label"
        placeholder="Все статусы"
        clearable
        style="min-width: 200px"
        @change="(v) => (filters.send_status = v)"
      />

      <CustomSelect
        :value="filters.resident_id"
        :options="residents"
        label="ЖК"
        option-value-key="resident_id"
        option-label-key="resident_name"
        placeholder="Все ЖК"
        clearable
        style="min-width: 220px"
        @change="(v) => (filters.resident_id = v)"
      />

      <CustomSelect
        :value="filters.contractor_id"
        :options="contractors"
        label="Контрагент"
        option-value-key="contractor_id"
        option-label-key="contractor_name"
        placeholder="Все контрагенты"
        clearable
        style="min-width: 220px"
        @change="(v) => (filters.contractor_id = v)"
      />
    </div>

    <q-linear-progress v-if="isLoading" indeterminate color="primary" class="tw-mb-2" />

    <CustomTable
      title="Статус отправки договоров в 1С"
      :rowsData="rowsData"
      :columns="contracts1cStatusColumns"
      :loading="isLoading"
      :small="true"
      :show-search="true"
      :custom-body="true"
      :sticky-header="false"
      :show-pagination="totalCount > LIMIT"
      :pages-number="Math.ceil(totalCount / LIMIT)"
      :count="totalCount"
      :on-pagination-change="onPageChange"
    >
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.contractor_agreement_list_id }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.agreement_num || '—' }}
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.document_date ? formatDate(props.row.document_date) : '—' }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.contractor_name || '—' }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.resident_name || '—' }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.project_type_name || '—' }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            <span class="tw-text-sm">{{ props.row.project_name || '—' }}</span>
          </custom-table-cell>

          <custom-table-cell align="right" vertical-align="center">
            <span style="white-space: nowrap">
              {{ props.row.agreement_sum != null ? numberWithCommas(props.row.agreement_sum) : '—' }}
            </span>
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            <q-badge
              v-if="props.row.send_status"
              :color="SEND_STATUS_MAP[props.row.send_status]?.color || 'grey'"
              :label="props.row.send_status_name"
            />
            <span v-else>—</span>
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.status_datetime ? formatDatetime(props.row.status_datetime) : '—' }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            <span
              v-if="props.row.error_text"
              class="tw-text-red-500 tw-text-xs tw-break-words"
              style="max-width: 300px; display: block"
            >
              {{ props.row.error_text }}
            </span>
            <span v-else>—</span>
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>

    <NotFoundText v-if="!isLoading && !rowsData.length" />
  </div>
</template>

<script>
import { numberWithCommas } from "@/services";
import {
  getContracts1cStatus,
  getResidentsForSelect,
  getContractorsForSelect,
  SEND_STATUS_OPTIONS,
  SEND_STATUS_MAP,
  contracts1cStatusColumns,
} from "./services";
import PageTitle from "@/components/common/PageTitle.vue";
import NotFoundText from "@/components/common/NotFoundText";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomSelect from "@/components/form/CustomSelect";
import CustomDateRange from "@/components/common/CustomDateRange";

export default {
  name: "ContractsDictionary",
  components: {
    PageTitle,
    NotFoundText,
    CustomTable,
    CustomTableCell,
    CustomSelect,
    CustomDateRange,
  },
  data() {
    return {
      rowsData: [],
      residents: [],
      contractors: [],
      isLoading: false,
      page: 1,
      totalCount: 0,
      LIMIT: 50,
      filters: {
        dateRange: { from: "", to: "" },
        send_status: null,
        resident_id: null,
        contractor_id: null,
      },
      SEND_STATUS_OPTIONS,
      SEND_STATUS_MAP,
      contracts1cStatusColumns,
    };
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.page = 1;
        this.loadData();
      },
    },
  },
  async mounted() {
    await Promise.all([this.loadData(), this.loadResidents(), this.loadContractors()]);
  },
  methods: {
    numberWithCommas,
    onDateRangeChange(range) {
      this.filters.dateRange = range;
    },
    onPageChange(page) {
      this.page = page;
      this.loadData();
    },
    toIsoDate(val) {
      if (!val) return null;
      const parts = val.split(".");
      if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
      return val;
    },
    buildParams() {
      const params = { limit: this.LIMIT, page: this.page };
      if (this.filters.dateRange?.from) params.date_from = this.toIsoDate(this.filters.dateRange.from);
      if (this.filters.dateRange?.to) params.date_to = this.toIsoDate(this.filters.dateRange.to);
      if (this.filters.send_status) params.send_status = this.filters.send_status;
      if (this.filters.resident_id) params.resident_id = this.filters.resident_id;
      if (this.filters.contractor_id) params.contractor_id = this.filters.contractor_id;
      return params;
    },
    async loadData() {
      this.isLoading = true;
      const res = await getContracts1cStatus(this.buildParams());
      this.isLoading = false;
      if (!res) return;
      this.rowsData = res.data;
      this.totalCount = res.total_count;
    },
    async loadResidents() {
      const res = await getResidentsForSelect();
      if (!res) return;
      this.residents = res;
    },
    async loadContractors() {
      const res = await getContractorsForSelect();
      if (!res) return;
      this.contractors = res;
    },
    formatDate(val) {
      if (!val) return "—";
      const d = new Date(val);
      if (isNaN(d.getTime())) return val;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
    },
    formatDatetime(val) {
      if (!val) return "—";
      const d = new Date(val);
      if (isNaN(d.getTime())) return val;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
  },
};
</script>

